/**
 * @file tabs.component.tsx
 * @path packages/ui/src/tabs.component.tsx
 * @description Responsive Neuromorphic Tabs.
 * UPDATED: Optimized for small viewports to prevent layout break.
 */

import { cn } from "@sirbastians/utils";
import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { buttonVariants } from "./button.component";

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      // 1. LAYOUT:
      // - 'flex' with 'w-full' ensures we use all available width.
      // - 'overflow-x-auto' + 'no-scrollbar' allows swiping if content is too wide.
      "inline-flex h-12 w-full items-center justify-start overflow-x-auto overflow-y-hidden rounded-xl p-1 gap-1 text-muted-foreground no-scrollbar select-none shrink-0",

      // 2. NEUROMORPHIC TRACK:
      "neuro-well bg-muted/10 border border-border/40",

      className,
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      // 1. BASE STYLES:
      buttonVariants({ variant: "secondary", size: "sm" }),

      // 2. RESPONSIVE SIZING & TYPOGRAPHY:
      // - 'flex-1' ensures all tabs share width equally on small screens.
      // - 'min-w-[80px]' prevents them from becoming illegibly small.
      // - 'text-[11px]' on mobile saves critical horizontal space.
      "h-full flex-1 md:flex-none md:min-w-[120px] px-2 sm:px-6 whitespace-nowrap transition-all duration-300",
      "text-[11px] sm:text-xs font-bold uppercase tracking-wider",

      // 3. NEUROMORPHIC ACTIVE STATE:
      "data-[state=active]:bg-accent",
      "data-[state=active]:text-white",
      "data-[state=active]:border-accent-foreground/10",
      "data-[state=active]:neuro-bezel",

      // 4. TOUCH OPTIMIZATION:
      "active:scale-95 touch-manipulation",

      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-4 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 animate-in fade-in-50 duration-300",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
